import { IDocumentClient } from "@multiweb/clients/documents";
import { ClientType, IClientFactory } from "@multiweb/clients/index";
import { IDocumentMetadata } from "@multiweb/types/contracts";
import { ISignatureDataStream } from "Cryptography/Crypto/CryptoInterfaces";
import { DIContainer } from "Global/Base/DIContainer";
import { HTTPRequest, IHTTPResult } from "../../Global/Base/Comunication/HTTPRequest";
import { HTTPStatus } from "../../Global/Base/Comunication/HTTPStatus";
import { InvalidArgumentException, InvalidOperationException, UserAbortedException } from "../../Global/Base/Exceptions";
import { OWManager } from "../../OW.Remoting/Remoting/OWManager";
import { RemotingDocumentReferenceUriGenerator } from "./RemotingDocumentReferenceUriGenerator";

/**
 * Implementace rozhraní streamu poskytujícího data potřebná pro vytváření podpisů pomocí standartního MultiWeb OW Remotingu
 */
export class DMSDocumentSignatureStream implements ISignatureDataStream {
    private readonly _instanceId: string;
    private readonly _attributeName: string;
    private readonly _prepareDownloadFunc?: (url: string) => Promise<boolean>;

    private _name: string | undefined;
    private readonly _documentClient: IDocumentClient;

    /**
     * Vytvoří nový stream poskytující data dokumentu z DMS a aplikační služby
     * @param instanceId Identifikátor instance objektu, ve které je hledaný atribut
     * @param attributeName Název atributu objektu, ve kterém je dokument uložen
     * @param prepareDownloadFunc Volitelná funkce umožňující předzpracování downloadu
     */
    constructor(instanceId: string, attributeName: string, prepareDownloadFunc?: (url: string) => Promise<boolean>) {
        if (!instanceId)
            throw new InvalidArgumentException("instanceId");
        if (!attributeName)
            throw new InvalidArgumentException("attributeName");

        this._instanceId = instanceId;
        this._attributeName = attributeName;
        this._prepareDownloadFunc = prepareDownloadFunc;
        this._documentClient = DIContainer.instance.create(IClientFactory).get(ClientType.document);
    }

    /**
     * Vygeneruje URI pro referenci ukazující na data v podpisu
     */
    public async getReferenceUri(): Promise<string> {
        return await new RemotingDocumentReferenceUriGenerator(await this.getName()).getReferenceUri();
    }

    /**
     * Načte název dat
     * @returns Název dat
     */
    public async getName(): Promise<string> {
        if (this._name === undefined)
            this._name = await this._documentClient.getDocumentName(this._instanceId, this._attributeName);

        return this._name;
    }

    /**
     * Načítá veškerá metadata ve formátu klíč - hodnota
     * @returns Metadata
     */
    public async getMetadata(): Promise<IDocumentMetadata | null> {
        return this._documentClient.getDocumentMetadata(this._instanceId, this._attributeName);
    }

    /**
     * Načítá celý obsah dokumentu. Stahuje po chuncích definované délky. Po stažení prvního zároveň odesílá
     * první chunk nativní aplikaci a stahuje druhý chunk, atd.
     * @param chunkSize Velikost, po které se mají data načítat
     * @param onChunkDownloaded Callback funkce, která je volaná při načtení části dat
     * @returns Data dokumentu
     */
    public async read(chunkSize: number, onChunkDownloaded: (chunk: ArrayBuffer, isLastChunk: boolean) => Promise<void>): Promise<void> {
        if (!onChunkDownloaded)
            throw new InvalidArgumentException("onChunkDownloaded");

        const url = await OWManager.getDocumentDownloadUrl(this._instanceId, this._attributeName, ""/* TODO: kontext */);
        if (!url)
            throw new InvalidOperationException(`Unable to get download url for document in attribute '${this._attributeName}' in instance '${this._instanceId}'`); //`

        if (this._prepareDownloadFunc && !(await this._prepareDownloadFunc(url)))
            throw new UserAbortedException();

        let contentFullLength = 0;
        let dataStartPosition = 0;
        let lastChunk = false; // příznak, zda se zpracovává poslední chunk
        let evenChunk = false; // příznak, který buffer se používá
        const dataContentBuffer = Array<IHTTPResult>(2); // buffery pro stahování

        const bufferChooser = (invert: boolean = false): number => {
            const bufferValue = ((!invert ? evenChunk : !evenChunk) ? 0 : 1);
            return bufferValue;
        };

        const getChunkFunction = async (bufferIndex: number) => {
            if ((contentFullLength !== 0) && (dataStartPosition + chunkSize >= contentFullLength)) {
                chunkSize = contentFullLength - dataStartPosition;
                lastChunk = true;
            }

            dataContentBuffer[bufferIndex] = await HTTPRequest.getWithWholeReponse(url, // request na první chunk souboru
                "arraybuffer",
                { "Range": `bytes=${dataStartPosition}-${(!lastChunk) ? dataStartPosition + chunkSize - 1 : ""}` }, // `
                [HTTPStatus.ok, HTTPStatus.partialContent, HTTPStatus.rangeNotSatisfiable]);
            if (dataContentBuffer[bufferIndex].status && dataContentBuffer[bufferIndex].status === HTTPStatus.rangeNotSatisfiable) { // pokud je soubor menší než chunkSize, stáhne se rovnou celý
                dataContentBuffer[bufferIndex] = await HTTPRequest.getWithWholeReponse(url, "arraybuffer");
                lastChunk = true;
            } else {
                const contentLength = dataContentBuffer[bufferIndex].response.byteLength; // velikost přijatého chunku
                dataStartPosition += contentLength;
                if (contentFullLength === 0) {
                    const req = dataContentBuffer[bufferIndex];
                    const contentRangeHeader = req.getResponseHeader("Content-Range");
                    if (contentRangeHeader) {
                        const rangeMatches = contentRangeHeader.match(/\s?bytes (\d*)-(\d*)\/(\d*)/); // z odpovědi z prvního přijatého chunku se parsne celková velikost souboru
                        if (rangeMatches)
                            contentFullLength = parseInt(rangeMatches[3]);
                    }
                }
            }
        };

        await getChunkFunction(bufferChooser()); // stažení prvního chunku

        do {
            evenChunk = !evenChunk;

            const promises = new Array<Promise<any>>();
            promises.push(onChunkDownloaded(dataContentBuffer[bufferChooser(true)].response, lastChunk));
            if (!lastChunk) // poslední chunk by se stahoval znovu
                promises.push(getChunkFunction(bufferChooser()));

            await Promise.all(promises); // čekání na odeslání části do nativky a na stažení následující části z DMS

            if (lastChunk && promises.length === 2)
                await onChunkDownloaded(dataContentBuffer[bufferChooser()].response, true);
        } while (!lastChunk);
    }
}
